// src/Services/Firebase/FirebaseConfig.js

/**
 * Firebase configuration file
 * Contains all the configuration for the Firebase app instance
 */

// Import the debug utility
import debug from '../../utils/debug'; // Path may need adjustment based on file location

// Default configuration for the main Firebase app
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Firebase Admin SDK configuration (for server-side operations)
const firebaseAdminConfig = {
  projectId: process.env.FIREBASE_ADMIN_PROJECT_ID,
  privateKey: process.env.FIREBASE_ADMIN_PRIVATE_KEY,
  clientEmail: process.env.FIREBASE_ADMIN_CLIENT_EMAIL,
};

// Additional service configuration
const serviceConfig = {
  // Google APIs configuration
  google: {
    apiKey: process.env.REACT_APP_GOOGLE_CALENDAR_API_KEY,
    clientId: process.env.REACT_APP_GOOGLE_CLIENT_ID,
    placesApiKey: process.env.REACT_APP_GOOGLE_PLACES_API_KEY
  },
  
  // TradeSafe API configuration for escrow payments.
  // SECURITY: the client secret is intentionally NOT included here. TradeSafe
  // authentication happens server-side via the Cloud Functions proxy — the client
  // never needs (and must never bundle) the client secret.
  tradeSafe: {
    apiUrl: process.env.REACT_APP_TRADESAFE_API_URL,
    clientId: process.env.REACT_APP_TRADESAFE_CLIENT_ID
  }
};

// Add configuration validation
const validateConfig = () => {
  const requiredFields = [
    "apiKey", 
    "authDomain", 
    "projectId", 
    "storageBucket", 
    "messagingSenderId", 
    "appId"
  ];
  
  const missingFields = requiredFields.filter(field => !firebaseConfig[field]);
  
  if (missingFields.length > 0) {
    // Use debug utility for logging validation errors
    debug.error('FirebaseConfig', 'Missing required configuration fields', {
      missingFields: missingFields.join(', ')
    });
    
    debug.log('FirebaseConfig', 'Please check your .env file and ensure all required variables are set.');
    return false;
  }
  
  // Log successful validation
  debug.log('FirebaseConfig', 'Configuration validated successfully');
  return true;
};

// Log environment information
const getCurrentEnvironment = () => {
  const environment = process.env.NODE_ENV || 'development';
  
  // Log environment information using debug utility
  debug.log('FirebaseConfig', `Running in ${environment} environment`);
  
  return environment;
};

// Initialize configuration with logging
const initializeConfig = () => {
  debug.component.mount('FirebaseConfig');
  
  const isValid = validateConfig();
  const environment = getCurrentEnvironment();
  
  // Log initialization status
  debug.firebase('Config', 'Firebase configuration initialized', {
    environment,
    isValid,
    projectId: firebaseConfig.projectId
  });
  
  return {
    isValid,
    environment
  };
};

// Call initialization when the module is imported
initializeConfig();

// Export configuration
export { 
  firebaseConfig, 
  firebaseAdminConfig,
  serviceConfig,
  getCurrentEnvironment,
  validateConfig
};

export default firebaseConfig;
